import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

import { recordThemeChange } from '@/contexts/analytics/themeTracking';
import {
  applyColorScheme, isDarkScheme, isThemeMode, resolveColorScheme, THEME_STORAGE_KEY,
  type ColorScheme, type ThemeMode,
} from '@/modern/theme/colorScheme';

// `system` follows the OS `prefers-color-scheme`.
export type ColorMode = ThemeMode;

type ColorModeContextValue = {
  // The user's selected preference — may be `system`. Use this to drive the
  // theme selector UI (which option is active).
  mode: ColorMode;
  // The resolved palette. When `mode` is `system` this reflects the OS.
  scheme: ColorScheme;
  isDark: boolean;
  toggleColorMode: () => void;
  setColorMode: (mode: ColorMode) => void;
};

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: 'light',
  scheme: 'light',
  isDark: false,
  toggleColorMode: () => {},
  setColorMode: () => {},
});

const getStoredMode = (): ColorMode | null => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(stored) ? stored : null;
  } catch {
    return null;
  }
};

const DARK_QUERY = '(prefers-color-scheme: dark)';

const systemIsDark = () => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia(DARK_QUERY).matches
);

export const ColorModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [mode, setMode] = useState<ColorMode>(() => getStoredMode() ?? 'light');
  const [osDark, setOsDark] = useState(systemIsDark);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setOsDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const setColorMode = useCallback((newMode: ColorMode) => {
    // Sync the Mixpanel `theme` property/event before persisting, so the change
    // is recorded with the correct previous value (read from localStorage).
    recordThemeChange(newMode);
    setMode(newMode);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newMode);
    } catch {
      // storage unavailable
    }
  }, []);

  const selectedMode: ColorMode = isThemeMode(mode) ? mode : 'light';
  const scheme = resolveColorScheme(selectedMode, osDark);
  const isDark = isDarkScheme(scheme);

  useEffect(() => {
    applyColorScheme(scheme);
  }, [scheme]);

  const toggleColorMode = useCallback(() => {
    setColorMode(isDark ? 'light' : 'dark');
  }, [isDark, setColorMode]);

  const value = useMemo<ColorModeContextValue>(
    () => ({
      mode: selectedMode, scheme, isDark, toggleColorMode, setColorMode,
    }),
    [selectedMode, scheme, isDark, toggleColorMode, setColorMode],
  );

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);
